import { uniq } from 'es-toolkit';

import { getUserVisibleOrDefaultExceptionMessage } from '@paragon/client-tools';
import { designService } from '@paragon/data-connections';
import { enqueueError, enqueueWarning } from '@paragon/notification-tools';
import { addSpinnerMessage, removeSpinnerMessage } from '@paragon/task-spinner';
import { notNullish, sortBy } from '@paragon/utilities';

import { getBundlesByScheduleLineGroupGuid } from '../selectors/bundles';
import { getScheduleLineGroupsForLineScheduleGridByLineGuid } from '../selectors/lineScheduleGrid';
import { getStatusesByScheduleLineGroupGuid } from '../selectors/scheduleLineGroups';
import { bundlesLoaded } from '../store/bundlesSlice';
import {
  scheduleLineGroupCreated,
  scheduleLineGroupDeleted,
  scheduleLineGroupsLoaded,
} from '../store/scheduleLineGroupsSlice';
import { setStatus } from './setStatus';
import { AsyncThunkActionVoid } from './thunkActions';

export function createScheduleLineGroup(
  bundleGuids: readonly string[],
  name: string,
  lineGuid: string,
  scheduledDate: string | null,
): AsyncThunkActionVoid {
  return async function createScheduleLineGroupThunkAction(dispatch, getState) {
    const { bundles } = getState();
    const bundlesForScheduleLineGroup = bundleGuids.map((bundleGuid) => bundles[bundleGuid]);
    const bundlesAreFromMultipleScheduleGroups =
      uniq(bundlesForScheduleLineGroup.map((bundle) => bundle.scheduleGroupGuid)).length > 1;
    if (bundlesAreFromMultipleScheduleGroups)
      throw new Error('When creating a schedule line group, bundles should come from one schedule group');

    const bundlesAlreadyAssignedToAScheduleLineGroup = bundlesForScheduleLineGroup
      .map((bundle) => bundle.scheduleLineGroupGuid)
      .filter(notNullish);
    if (bundlesAlreadyAssignedToAScheduleLineGroup.length > 0)
      throw new Error('Bundles are already assigned to a schedule line group');

    const spinnerId = dispatch(addSpinnerMessage('Creating schedule line group...'));

    try {
      const scheduleLineGroup = await designService.scheduleLineGroups.insert(name, lineGuid, scheduledDate);
      dispatch(scheduleLineGroupCreated(scheduleLineGroup));
      await dispatch(addToScheduleLineGroup(scheduleLineGroup.guid, bundleGuids, false));
    } catch (exception) {
      console.error(exception);
      dispatch(enqueueError('Error creating schedule line group'));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

export function addToScheduleLineGroup(
  scheduleLineGroupGuid: string,
  bundleGuids: readonly string[],
  spinner = true,
): AsyncThunkActionVoid {
  return async function addToScheduleLineGroupThunkAction(dispatch, getState) {
    const { bundles } = getState();
    const bundlesForScheduleLineGroup = bundleGuids.map((bundleGuid) => bundles[bundleGuid]);
    const scheduleGroupGuidsForBundlesToAdd = uniq(
      bundlesForScheduleLineGroup.map((bundle) => bundle.scheduleGroupGuid).filter(notNullish),
    );
    const bundlesAreFromMultipleScheduleGroups = scheduleGroupGuidsForBundlesToAdd.length > 1;
    if (bundlesAreFromMultipleScheduleGroups)
      throw new Error('When adding to a schedule line group, bundles should come from one schedule group');

    const existingScheduleGroupGuids = uniq(
      getBundlesByScheduleLineGroupGuid(getState())
        [scheduleLineGroupGuid].map((bundle) => bundle.scheduleGroupGuid)
        .filter(notNullish),
    );
    if (
      existingScheduleGroupGuids.length > 0 &&
      !scheduleGroupGuidsForBundlesToAdd.every((scheduleGroupGuidForBundleToAdd) =>
        existingScheduleGroupGuids.includes(scheduleGroupGuidForBundleToAdd),
      )
    )
      throw new Error(
        'Adding bundles to a schedule line group such that it will then contain bundles from multiple schedule groups is not allowed',
      );

    const bundlesAlreadyAssignedToAScheduleLineGroup = bundlesForScheduleLineGroup
      .map((bundle) => bundle.scheduleLineGroupGuid)
      .filter(notNullish);
    if (bundlesAlreadyAssignedToAScheduleLineGroup.length > 0)
      throw new Error('Bundles are already assigned to a schedule line group');

    const spinnerId = spinner ? dispatch(addSpinnerMessage('Adding to schedule line group...')) : null;

    try {
      // We do not know what order the bundle GUIDs are in. In order to provide some level of predictable deterministic
      // behavior, order the bundles by ID. This only applies to these bundles being added now; any already added or to
      // be added in the future will have this sorting applied as well, but it will not re-apply the sorting to the
      // whole ScheduleLineGroup. In the future, we may want this sorting to match the way the Schedule Grid is sorted.
      const bundleGuidsOrderedById = sortBy(bundleGuids, (bundleGuid) => bundles[bundleGuid].id);
      const updatedBundles = await designService.bundles.assignToScheduleLineGroup(
        bundleGuidsOrderedById,
        scheduleLineGroupGuid,
      );
      dispatch(bundlesLoaded(updatedBundles));
      await dispatch(setStatus(bundleGuids, 'Scheduled', false));
    } catch (exception) {
      dispatch(
        enqueueError(getUserVisibleOrDefaultExceptionMessage(exception, 'Error adding bundles to schedule line group')),
      );
    } finally {
      if (spinnerId != null) dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

export function editScheduleLineGroup(
  guid: string,
  name: string,
  lineGuid: string,
  scheduledDate: string | null,
): AsyncThunkActionVoid {
  return async function editScheduleLineGroupThunkAction(dispatch, getState) {
    const spinnerId = dispatch(addSpinnerMessage('Editing schedule line group...'));

    try {
      const existingScheduleLineGroup = getState().scheduleLineGroups[guid];
      const updatedScheduleLineGroup = {
        ...existingScheduleLineGroup,
        name,
        lineGuid,
        scheduledDate,
      };

      await designService.scheduleLineGroups.update(updatedScheduleLineGroup);
      dispatch(scheduleLineGroupsLoaded([updatedScheduleLineGroup]));
    } catch {
      dispatch(enqueueError('Error editing schedule line group'));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

export function removeBundleFromScheduleLineGroup(bundleGuid: string): AsyncThunkActionVoid {
  return async function removeBundleFromScheduleLineGroupThunkAction(dispatch, getState) {
    const scheduleLineGroupGuid = getState().bundles[bundleGuid].scheduleLineGroupGuid;
    if (scheduleLineGroupGuid === null)
      throw new Error('Attempted to remove bundle from schedule line group when it was not assigned to one');

    const spinnerId = dispatch(addSpinnerMessage('Removing bundle from schedule line group...'));

    try {
      const updatedBundles = await designService.bundles.removeFromScheduleLineGroup(bundleGuid);
      dispatch(bundlesLoaded(updatedBundles));
      await dispatch(setStatus([bundleGuid], 'Unscheduled', false));

      const statusesByScheduleLineGroupGuid = getStatusesByScheduleLineGroupGuid(getState());
      if (statusesByScheduleLineGroupGuid[scheduleLineGroupGuid].some((status) => status !== 'Scheduled')) {
        dispatch(
          enqueueWarning(
            'Bundle removed, but other bundle(s) in this Schedule Line Group have a status other than "Scheduled". You may need to alert the shop to refresh to receive the updates.',
          ),
        );
      }
    } catch (exception) {
      dispatch(
        enqueueError(
          getUserVisibleOrDefaultExceptionMessage(exception, 'Error removing bundle from schedule line group'),
        ),
      );
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

export function deleteScheduleLineGroup(scheduleLineGroupGuid: string): AsyncThunkActionVoid {
  return async function deleteScheduleLineGroupThunkAction(dispatch) {
    const spinnerId = dispatch(addSpinnerMessage('Removing schedule line group...'));

    try {
      const updatedBundles = await designService.scheduleLineGroups.remove(scheduleLineGroupGuid);
      dispatch(scheduleLineGroupDeleted({ scheduleLineGroupGuid, updatedBundles }));
      await dispatch(
        setStatus(
          updatedBundles.map((bundle) => bundle.guid),
          'Unscheduled',
          false,
        ),
      );
    } catch (exception) {
      dispatch(enqueueError(getUserVisibleOrDefaultExceptionMessage(exception, 'Error removing schedule line group')));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

export function updatePriorities(scheduleLineGroupGuid: string, direction: 'up' | 'down'): AsyncThunkActionVoid {
  return async function updatePrioritiesThunkAction(dispatch, getState) {
    const spinnerId = dispatch(addSpinnerMessage('Updating priorities of schedule groups...'));

    const { lineGuid } = getState().scheduleLineGroups[scheduleLineGroupGuid];
    const scheduleLineGroupsInScheduleGrid = getScheduleLineGroupsForLineScheduleGridByLineGuid(getState())[lineGuid];
    const indexOfScheduleLineGroup = scheduleLineGroupsInScheduleGrid.findIndex(
      (scheduleLineGroup) => scheduleLineGroup.guid === scheduleLineGroupGuid,
    );
    if (indexOfScheduleLineGroup === -1) throw new Error('Schedule group not found while updating priorities');
    const scheduleLineGroup = scheduleLineGroupsInScheduleGrid[indexOfScheduleLineGroup];

    const indexOfDisplacedScheduleLineGroup =
      direction === 'up' ? indexOfScheduleLineGroup - 1 : indexOfScheduleLineGroup + 1;
    const displacedScheduleLineGroup = scheduleLineGroupsInScheduleGrid[indexOfDisplacedScheduleLineGroup];

    const statusesByScheduleLineGroupGuid = getStatusesByScheduleLineGroupGuid(getState());
    const statusesForScheduleLineGroup = statusesByScheduleLineGroupGuid[scheduleLineGroupGuid];
    const statusesForDisplacedScheduleLineGroup = statusesByScheduleLineGroupGuid[displacedScheduleLineGroup.guid];
    if (
      statusesForScheduleLineGroup
        .concat(statusesForDisplacedScheduleLineGroup)
        .some((status) => status !== 'Scheduled')
    ) {
      dispatch(
        enqueueWarning(
          'Priority change for schedule line group with status(es) other than "Scheduled". Communicate this change directly to the shop as well.',
        ),
      );
    }

    // Swap the priority values of the schedule groups
    const updatedPriorities = {
      [scheduleLineGroupGuid]: displacedScheduleLineGroup.priority,
      [displacedScheduleLineGroup.guid]: scheduleLineGroup.priority,
    };

    try {
      const updatedScheduleLineGroups = await designService.scheduleLineGroups.updatePriorities(updatedPriorities);
      dispatch(scheduleLineGroupsLoaded(updatedScheduleLineGroups));
    } catch {
      dispatch(enqueueError('Error updating priorities of schedule line groups'));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}
